import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Send, FileText, Loader2, RefreshCw } from "lucide-react";
import { admin, setToken, apiError } from "@/lib/admin";
import { LeadRow } from "./LeadRow";
import { WeeklyChart } from "./WeeklyChart";

const TABS = [
  ["inquiries", "Inquiries"],
  ["deck_requests", "Deck downloads"],
];

export const AdminDashboard = ({ onLogout }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("inquiries");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await admin.get("/admin/leads");
      setData(data);
    } catch (err) {
      if (err?.response?.status === 401) return onLogout();
      toast.error(apiError(err));
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const onStatus = async (lead, status) => {
    setData((d) => ({ ...d, [tab]: d[tab].map((l) => (l.id === lead.id ? { ...l, status } : l)) }));
    try {
      await admin.patch(`/admin/leads/${tab}/${lead.id}`, { status });
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const onDelete = async (lead) => {
    setData((d) => ({ ...d, [tab]: d[tab].filter((l) => l.id !== lead.id) }));
    try {
      await admin.delete(`/admin/leads/${tab}/${lead.id}`);
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const run = async (key, path, ok) => {
    setBusy(key);
    try {
      const { data } = await admin.post(path);
      toast.success(ok(data));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy("");
    }
  };

  const logout = () => {
    setToken(null);
    onLogout();
  };

  const rows = (data?.[tab] || []).filter((l) => filter === "all" || (filter === "new" ? l.status !== "contacted" : l.status === "contacted"));
  const pending = (k) => (data?.[k] || []).filter((l) => l.status !== "contacted").length;

  return (
    <div className="min-h-screen pb-20" data-testid="admin-dashboard">
      <header className="border-b border-gold/15 bg-ink">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-12 h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-baseline gap-3" data-testid="admin-home-link">
            <span className="font-serif text-2xl text-ivory">Bossard</span>
            <span className="eyebrow text-ivory/60">Leads</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={load} className="p-2 text-ivory/60 hover:text-gold" title="Refresh" data-testid="admin-refresh-btn">
              <RefreshCw size={15} />
            </button>
            <button
              onClick={() => run("digest", "/admin/digest/send", (d) => (d.sent ? `Digest sent — ${d.inquiries} inquiries, ${d.deck_requests} downloads pending.` : "Digest could not be sent. Check email settings."))}
              disabled={!!busy}
              className="inline-flex items-center gap-2 border border-gold/50 text-gold text-[10px] tracking-[0.18em] uppercase px-3 py-2.5 hover:border-gold disabled:opacity-50"
              data-testid="admin-digest-btn"
            >
              {busy === "digest" ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} <span className="hidden sm:inline">Send digest now</span>
            </button>
            <button
              onClick={() => run("deck", "/admin/deck/regenerate", () => "Investor deck regenerated from deck_content.json.")}
              disabled={!!busy}
              className="inline-flex items-center gap-2 border border-ivory/20 text-ivory/70 text-[10px] tracking-[0.18em] uppercase px-3 py-2.5 hover:border-gold hover:text-gold disabled:opacity-50"
              data-testid="admin-deck-btn"
            >
              {busy === "deck" ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />} <span className="hidden sm:inline">Regenerate deck</span>
            </button>
            <button onClick={logout} className="p-2 text-ivory/60 hover:text-gold" title="Sign out" data-testid="admin-logout-btn">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 lg:px-12 pt-12">
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {[
            ["Awaiting reply", pending("inquiries") + pending("deck_requests"), "gold"],
            ["Inquiries", data?.inquiries?.length ?? "—"],
            ["Deck downloads", data?.deck_requests?.length ?? "—"],
          ].map(([k, v, c]) => (
            <div key={k} className="border border-gold/15 bg-ink p-6" data-testid={`admin-stat-${k.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="eyebrow">{k}</div>
              <div className={`mt-2 font-serif text-4xl ${c ? "text-gold" : "text-ivory"}`}>{data ? v : "—"}</div>
            </div>
          ))}
        </div>

        {data && <WeeklyChart inquiries={data.inquiries} deckRequests={data.deck_requests} />}

        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gold/15">
          <div className="flex gap-8">
            {TABS.map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`relative pb-4 text-sm tracking-wide transition-colors ${tab === k ? "text-ivory" : "text-ivory/50 hover:text-ivory"}`}
                data-testid={`admin-tab-${k}`}
              >
                {label}
                {pending(k) > 0 && <span className="ml-2 font-mono text-[10px] text-gold">{pending(k)}</span>}
                {tab === k && <span className="absolute left-0 right-0 -bottom-px h-px bg-gold" />}
              </button>
            ))}
          </div>
          <div className="flex gap-1 pb-3">
            {["all", "new", "contacted"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`font-mono text-[10px] tracking-[0.18em] uppercase px-3 py-1.5 border transition-colors ${filter === f ? "border-gold text-gold" : "border-transparent text-ivory/40 hover:text-ivory"}`}
                data-testid={`admin-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          <div className="py-20 text-center text-ivory/40" data-testid="admin-loading">
            <Loader2 size={18} className="animate-spin inline" />
          </div>
        ) : rows.length === 0 ? (
          <p className="py-20 text-center text-ivory/40" data-testid="admin-empty">
            No leads here.
          </p>
        ) : (
          <ul data-testid="admin-lead-list">
            {rows.map((l) => (
              <LeadRow key={l.id} lead={l} kind={tab} onStatus={onStatus} onDelete={onDelete} />
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};
